import { api } from '@goaltree/backend/convex/_generated/api'
import { REVIEW_TEXT_MAX } from '@goaltree/backend/convex/lib/reviewPrompt'
import { Button } from '@goaltree/ui/components/button'
import { Checkbox } from '@goaltree/ui/components/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@goaltree/ui/components/dialog'
import { cn } from '@goaltree/ui/lib/utils'
import type { OptimisticLocalStore } from 'convex/browser'
import { useMutation, useQuery } from 'convex/react'
import { Star } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { onPositiveMoment } from '@/lib/positive-moment'
import { patchPrefs } from '@/lib/prefs-optimistic'

const DELAY_MS = 1500
const REQUESTED_DELAY_MS = 4000

function clearPromptState(store: OptimisticLocalStore) {
  if (store.getQuery(api.reviews.getPromptState, {}))
    store.setQuery(api.reviews.getPromptState, {}, { eligible: false, requested: false })
}

// Asks for a review once, right after a positive moment, when the server says
// this user is eligible (established, active, not asked recently).
export function ReviewPromptHost() {
  const state = useQuery(api.reviews.getPromptState)
  const markShown = useMutation(api.reviews.markPromptShown).withOptimisticUpdate((store) => {
    clearPromptState(store)
    patchPrefs(store, { reviewPromptAt: Date.now(), reviewRequestedAt: undefined })
  })
  const [open, setOpen] = useState(false)
  const asked = useRef(false)
  const eligible = useRef(false)
  eligible.current = !!state?.eligible

  useEffect(
    () =>
      onPositiveMoment(() => {
        if (asked.current || !eligible.current) return
        asked.current = true
        setTimeout(() => {
          setOpen(true)
          void markShown({}).catch(() => {})
        }, DELAY_MS)
      }),
    [markShown],
  )

  // Asked for personally: show soon after opening the app, no moment needed.
  const requested = !!state?.requested
  useEffect(() => {
    if (!requested || asked.current) return
    const id = setTimeout(() => {
      asked.current = true
      setOpen(true)
      void markShown({}).catch(() => {})
    }, REQUESTED_DELAY_MS)
    return () => clearTimeout(id)
  }, [requested, markShown])

  return <ReviewDialog open={open} onOpenChange={setOpen} prompted />
}

export function ReviewDialog({
  open,
  onOpenChange,
  prompted = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Auto-shown (vs opened from Settings): offers "Don't ask again".
  prompted?: boolean
}) {
  const { t } = useTranslation()
  const mine = useQuery(api.reviews.getMine, open ? {} : 'skip')
  const submit = useMutation(api.reviews.submit).withOptimisticUpdate((store, args) => {
    store.setQuery(
      api.reviews.getMine,
      {},
      {
        rating: args.rating,
        text: args.text?.trim().slice(0, REVIEW_TEXT_MAX) ?? '',
        consent: args.consent,
        showPhoto: args.consent && args.showPhoto,
      },
    )
    clearPromptState(store)
    patchPrefs(store, { reviewPromptAt: Date.now() })
  })
  const optOut = useMutation(api.reviews.optOut).withOptimisticUpdate((store) => {
    clearPromptState(store)
    patchPrefs(store, { reviewPromptOptOut: true })
  })
  const [rating, setRating] = useState(0)
  const [text, setText] = useState('')
  const [consent, setConsent] = useState(false)
  const [showPhoto, setShowPhoto] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open || !mine) return
    setRating(mine.rating)
    setText(mine.text)
    setConsent(mine.consent)
    setShowPhoto(mine.showPhoto || !mine.consent)
  }, [open, mine])

  async function send() {
    if (!rating || busy) return
    setBusy(true)
    try {
      await submit({ rating, text, consent, showPhoto, platform: 'web' })
      toast.success(t('appReview.thanks'))
      onOpenChange(false)
    } catch {
      toast.error(t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('appReview.title')}</DialogTitle>
          <p className="text-sm text-muted-foreground">{t('appReview.subtitle')}</p>
        </DialogHeader>
        <div className="flex justify-center gap-1.5 py-1" role="radiogroup">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={rating === n}
              aria-label={t('appReview.starsLabel', { count: n })}
              onClick={() => setRating(n)}
              className="cursor-pointer rounded-md p-1 transition-transform hover:scale-110 active:scale-95"
            >
              <Star
                className={cn(
                  'size-8 transition-colors',
                  n <= rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40',
                )}
                strokeWidth={1.8}
              />
            </button>
          ))}
        </div>
        <div className="relative">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={REVIEW_TEXT_MAX}
            rows={3}
            placeholder={t('appReview.placeholder')}
            className="w-full resize-none rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/45 focus:outline-none"
          />
          {text.length > REVIEW_TEXT_MAX - 100 && (
            <span className="absolute right-2.5 bottom-2 text-[11px] text-muted-foreground tabular-nums">
              {REVIEW_TEXT_MAX - text.length}
            </span>
          )}
        </div>
        <label className="flex cursor-pointer items-start gap-2.5 text-sm text-foreground">
          <Checkbox checked={consent} onCheckedChange={(c) => setConsent(!!c)} className="mt-0.5" />
          <span>{t('appReview.consent')}</span>
        </label>
        {consent && (
          <label className="-mt-1 flex cursor-pointer items-start gap-2.5 pl-6 text-sm text-muted-foreground">
            <Checkbox
              checked={showPhoto}
              onCheckedChange={(c) => setShowPhoto(!!c)}
              className="mt-0.5"
            />
            <span>{t('appReview.showPhoto')}</span>
          </label>
        )}
        <div className="flex flex-col gap-2 pt-1">
          <Button onClick={send} disabled={!rating || busy}>
            {t('appReview.submit')}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('appReview.notNow')}
          </Button>
          {prompted && (
            <button
              type="button"
              onClick={() => {
                void optOut({}).catch(() => {})
                onOpenChange(false)
              }}
              className="cursor-pointer text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('appReview.dontAsk')}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
